'use client'

import * as React from 'react'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'
import { Link, useLocation } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/components/ui/sidebar'

export function NavProjects({
  projects,
}: {
  projects: {
    name: string
    url: any
    icon: Record<string, React.ReactNode>
    isActive: boolean
    items?: {
      title: string
      url: string
      isActive: boolean
      default: React.ReactNode
      active: React.ReactNode
    }[]
  }[]
}) {
  const {} = useSidebar()

  const location = useLocation()
  const [openItems, setOpenItems] = React.useState<Record<string, boolean>>({})

  const isActiveLink = (url: string) => {
    return location.pathname === url || location.pathname.startsWith(`${url}/`)
  }

  const isAnySubItemActive = (items: any[] | undefined) => {
    return items?.some((subItem) => isActiveLink(subItem.url)) || false
  }

  // Initialize and update open state based on active children
  React.useEffect(() => {
    setOpenItems((prev) => {
      const newState: Record<string, boolean> = { ...prev }
      projects.forEach((item) => {
        if (item.items) {
          const hasActiveChild = isAnySubItemActive(item.items)
          // If has active child, ensure it's open
          if (hasActiveChild) {
            newState[item.name] = true
          }
          // If no active child and not manually opened, keep current state or default to false
          else if (newState[item.name] === undefined) {
            newState[item.name] = false
          }
        }
      })
      return newState
    })
  }, [location.pathname, projects])

  const handleOpenChange = (itemName: string, isOpen: boolean) => {
    setOpenItems((prev) => {
      const newState: Record<string, boolean> = { ...prev }
      // Close all other items (except those with active children)
      projects.forEach((item) => {
        if (item.items && item.name !== itemName) {
          const hasActiveChild = isAnySubItemActive(item.items)
          newState[item.name] = hasActiveChild // Keep open if has active child
        }
      })
      // Set the clicked item's state
      newState[itemName] = isOpen
      return newState
    })
  }

  return (
    <SidebarGroup className='mt-4 group-data-[collapsible=icon]:hidden'>
      <SidebarMenu>
        {projects.map((item) =>
          !item?.items ? (
            <SidebarMenuItem key={item.name} className={cn('mb-2')}>
              <SidebarMenuButton
                asChild
                className={cn(
                  'py-5 pl-4 rounded-none hover:bg-[#F6FFFC] border-l-3 border-l-transparent hover:border-l-[#295532]',
                  isActiveLink(item.url) &&
                    'bg-[#F6FFFC] border-l-3 border-l-[#295532]'
                )}
              >
                <Link to={item.url}>
                  {isActiveLink(item.url)
                    ? item.icon.active
                    : item.icon.default}
                  <span>{item.name}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : (
            <SidebarMenu>
              {projects
                .filter((item) => item?.items && item.items?.length > 0)
                .map((item) => {
                  const isOpen = openItems[item.name] ?? false
                  const hasActiveChild = isAnySubItemActive(item.items)
                  // Keep open if it has active child, otherwise use controlled state
                  const shouldBeOpen = hasActiveChild || isOpen

                  return (
                    <Collapsible
                      key={item.name}
                      asChild
                      open={shouldBeOpen}
                      onOpenChange={(open) => handleOpenChange(item.name, open)}
                      className='group/collapsible'
                    >
                      <SidebarMenuItem>
                        <CollapsibleTrigger
                          asChild
                          className={cn(
                            'py-5 pl-4 rounded-none hover:bg-[#F6FFFC] border-l-3 border-l-transparent hover:border-l-[#295532]',
                            (isActiveLink(item.url) || hasActiveChild) &&
                              'bg-[#F6FFFC] border-l-3 border-l-[#295532]'
                          )}
                        >
                          <SidebarMenuButton tooltip={item.name}>
                            {isActiveLink(item.url) || hasActiveChild
                              ? item.icon.active
                              : item.icon.default}
                            <span>{item.name}</span>
                            <ChevronRight className='ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90' />
                          </SidebarMenuButton>
                        </CollapsibleTrigger>
                        <CollapsibleContent className='pl-4'>
                          <SidebarMenuSub>
                            {item.items?.map((subItem) => (
                              <SidebarMenuSubItem
                                key={subItem.title}
                                className='relative'
                              >
                                <SidebarMenuSubButton
                                  asChild
                                  className={cn(
                                    'hover:bg-transparent hover:text-primary',
                                    isActiveLink(subItem.url) && 'text-primary'
                                  )}
                                  onClick={() => {
                                    // Ensure parent stays open when child is clicked
                                    if (!openItems[item.name]) {
                                      handleOpenChange(item.name, true)
                                    }
                                  }}
                                >
                                  <Link to={subItem.url}>
                                    <span>{subItem.title}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                                {isActiveLink(subItem.url) ? (
                                  <span className='w-2 h-2 rounded-full bg-primary absolute -left-[.9rem] top-[.64rem]'></span>
                                ) : (
                                  <span className='w-2 h-2 rounded-full bg-gray-300 absolute -left-[.9rem] top-[.64rem]'></span>
                                )}
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  )
                })}
            </SidebarMenu>
          )
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}
